export interface ChargingStation{
    id: string,
    lat: number,
    lon: number,
    plugs: Plug[],
    img: string,
    types: string,
    maxpower: number,
    opid: number,
    transparentOp?: boolean,
    isFavStation?: boolean,
    isDisabledStation?: boolean,
    isFavOperator?: boolean,
    isDisabledOperator?: boolean,
    minZoomLevel: number
    // details
    /*plugs: Plug[],
    cost: string,
    email: string,
    
    opid: number,
    phone: string,
    title: string,*/
}

export interface ChargingGroup{
    lat: number,
    lon: number,
    maxpower: number,
    powerid: number,
    count: number
}

export interface Plug{
    type: number,
    power: number,
    count?: number,
    statusCount?: number,
    available?: number
}

export interface ChargingStationDetails{
    id: string,
    provider: ChargerProvider,
    lat: number,
    lon: number,
    plugs: Plug[],
    img: string,
    opid: number,
    cost: string,
    email: string,
    phone: string,
    title: string
}

export const enum ChargerProvider {
    OCM,
    Ecomovement
  }

/** Above this a charger is fast (purple). */
export const FAST_CHARGER_POWER_KW = 35;
/** Above this a charger is medium (green); at or below it, slow (yellow). 11 kW is the top of home/AC wallboxes. */
export const MEDIUM_CHARGER_POWER_KW = 11;

/**
 * Power class of a charger, 1 slow, 2 medium, 3 fast. Map pins and route stops must both use
 * this so a charger keeps the same colour everywhere.
 */
export function chargerPowerClass(powerKw: number): 1 | 2 | 3 {
    if (powerKw > FAST_CHARGER_POWER_KW) {
        return 3;
    }
    if (powerKw > MEDIUM_CHARGER_POWER_KW) {
        return 2;
    }
    return 1;
}

/** The car's charge limits; 0 means unknown, so the plug's own power stands. */
export interface CarChargeLimits {
    ChargePower: number,
    FastChargePower: number
}

/** Plug types the car charges on with its AC ChargePower; the rest use FastChargePower. */
const AC_PLUG_TYPES = [3, 5, 6];

/** The most the car can draw on this plug type, 0 when unknown. */
export function carPlugPower(plugType: number, car: CarChargeLimits | null | undefined): number {
    if (!car) {
        return 0;
    }
    return (AC_PLUG_TYPES.includes(plugType) ? car.ChargePower : car.FastChargePower) || 0;
}

/**
 * The best power this car gets from any of the plugs, each capped at the car's limit for that
 * plug type. This is what the routing reports for a stop, so pins colour by it to match.
 */
export function chargePowerForCar(plugs: Plug[], car: CarChargeLimits | null | undefined): number {
    let best = 0;
    for (const plug of plugs) {
        const carPower = carPlugPower(plug.type, car);
        const power = carPower > 0 ? Math.min(plug.power, carPower) : plug.power;
        if (power > best) {
            best = power;
        }
    }
    return best;
}

/** How one plug's power compares with the car's limit on it. */
export interface PlugPowerSplit {
    /** What the car charges at: the lower of the two, or the plug power when the car limit is unknown. */
    effective: number,
    /** Which side caps the power; 'unknown' without a car limit, 'none' when both match. */
    limitedBy: 'car' | 'charger' | 'none' | 'unknown'
}

export function plugPowerSplit(plugPower: number, carPower: number): PlugPowerSplit {
    if (carPower <= 0) {
        return { effective: plugPower, limitedBy: 'unknown' };
    }
    if (carPower < plugPower) {
        return { effective: carPower, limitedBy: 'car' };
    }
    if (plugPower < carPower) {
        return { effective: plugPower, limitedBy: 'charger' };
    }
    return { effective: plugPower, limitedBy: 'none' };
}

/**
 * A cluster only carries its strongest plug's power, not the plug types, so the cap is the
 * car's best limit on any plug.
 */
export function groupChargePowerForCar(maxpower: number, car: CarChargeLimits | null | undefined): number {
    const carBest = Math.max(car?.ChargePower || 0, car?.FastChargePower || 0);
    return carBest > 0 ? Math.min(maxpower, carBest) : maxpower;
}
